import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, InlineNotification, Stack } from "@carbon/react";
import type { ImportFormatResponse } from "../../types";

/** When a file was last read through a format, in words rather than a timestamp. */
const usedOn = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : null;

/**
 * "We have read this file before": the file matched a saved format, so there is nothing to fill in.
 *
 * The description somebody wrote the first time is kept out of the way until asked for — it is
 * reassurance, not a question.
 */
const SavedFormatNotice = ({ format }: { format: ImportFormatResponse }) => {
  const { t } = useTranslation();
  const [showSaved, setShowSaved] = useState(false);

  return (
    <Stack gap={3}>
      <InlineNotification
        kind="success"
        lowContrast
        hideCloseButton
        className="pr-import__notification"
        title={t("purchaseRequests.import.recognised")}
      >
        <Stack gap={3}>
          <p>
            {t("purchaseRequests.import.recognisedDetail", {
              name: format.name,
              version: format.versionNumber,
            })}
          </p>
          {format.useCount > 0 && (
            <p>
              {t("purchaseRequests.import.recognisedUse", {
                count: format.useCount,
                date: usedOn(format.lastUsedAt) ?? "",
              })}
            </p>
          )}
          <p>{t("purchaseRequests.import.recognisedNext")}</p>
        </Stack>
      </InlineNotification>

      {/* Outside the notification: Carbon notifications may not hold interactive children. */}
      {format.userDescription && (
        <div>
          <Button
            kind="ghost"
            size="sm"
            aria-expanded={showSaved}
            onClick={() => setShowSaved((open) => !open)}
          >
            {t("purchaseRequests.import.showSavedDescription")}
          </Button>
          {showSaved && (
            <pre className="pr-import__saved-description">
              {format.userDescription}
            </pre>
          )}
        </div>
      )}
    </Stack>
  );
};

export default SavedFormatNotice;
